/**
 * Superadmin UI kit: the shared building blocks every platform page uses so
 * spacing, surfaces, tags and tables look the same everywhere.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { ChevronLeft, ChevronRight, Search, type LucideIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

// ── Tones ─────────────────────────────────────────────────────────────────────

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "violet"

/** Text + tinted background for small chips/icons. One palette for the whole panel. */
export const TONE: Record<Tone, string> = {
  neutral: "text-[hsl(225_6%_72%)] bg-[hsl(228_8%_14%)]",
  accent: "text-[hsl(224_100%_80%)] bg-[hsl(224_70%_16%)]",
  success: "text-[hsl(152_60%_66%)] bg-[hsl(152_45%_12%)]",
  warning: "text-[hsl(38_92%_70%)] bg-[hsl(38_45%_13%)]",
  danger: "text-[hsl(358_90%_76%)] bg-[hsl(358_45%_15%)]",
  violet: "text-[hsl(258_100%_84%)] bg-[hsl(258_45%_18%)]",
}

const DOT: Record<Tone, string> = {
  neutral: "bg-[hsl(225_6%_55%)]",
  accent: "bg-[hsl(224_100%_66%)]",
  success: "bg-[hsl(152_60%_52%)]",
  warning: "bg-[hsl(38_92%_62%)]",
  danger: "bg-[hsl(358_82%_62%)]",
  violet: "bg-[hsl(258_90%_72%)]",
}

// ── Page scaffolding ──────────────────────────────────────────────────────────

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-foreground">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-[13px] text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Page({ children }: { children: ReactNode }) {
  return <div className="space-y-6">{children}</div>
}

export function Panel({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
  flush,
}: {
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  /** No body padding (tables, lists that touch the edges). */
  flush?: boolean
}) {
  return (
    <section className={cn("min-w-0 overflow-hidden rounded-xl border border-border bg-card", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            {title && <h2 className="truncate text-[14px] font-semibold text-foreground">{title}</h2>}
            {description && <p className="mt-0.5 text-[12px] text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn(!flush && "p-4", bodyClassName)}>{children}</div>
    </section>
  )
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export function StatGrid({ children, cols = 4 }: { children: ReactNode; cols?: 3 | 4 }) {
  return <div className={cn("grid grid-cols-1 gap-3 min-[480px]:grid-cols-2", cols === 4 ? "xl:grid-cols-4" : "lg:grid-cols-3")}>{children}</div>
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "neutral",
  hintTone,
  info,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon?: LucideIcon
  tone?: Tone
  hintTone?: Tone
  info?: string
}) {
  const card = (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[12px] font-medium text-muted-foreground">{label}</span>
        {Icon && (
          <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-md", TONE[tone])}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      <div className="mt-3 text-[22px] font-semibold leading-none tracking-tight tabular-nums text-foreground">{value}</div>
      {hint && <div className={cn("mt-1.5 truncate text-[12px]", hintTone ? TONE[hintTone].split(" ")[0] : "text-muted-foreground")}>{hint}</div>}
    </div>
  )
  if (!info) return card
  return (
    <Tooltip>
      <TooltipTrigger asChild><div>{card}</div></TooltipTrigger>
      <TooltipContent className="max-w-64 text-xs">{info}</TooltipContent>
    </Tooltip>
  )
}

// ── Chips ─────────────────────────────────────────────────────────────────────

export function Tag({ tone = "neutral", dot, children, className }: { tone?: Tone; dot?: boolean; children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[11px] font-medium", TONE[tone], className)}>
      {dot && <span className={cn("h-1.5 w-1.5 rounded-full", DOT[tone])} />}
      {children}
    </span>
  )
}

export function IconTile({ icon: Icon, tone = "neutral", className }: { icon: LucideIcon; tone?: Tone; className?: string }) {
  return (
    <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", TONE[tone], className)}>
      <Icon className="h-4 w-4" />
    </span>
  )
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex flex-col gap-2 border-b border-border p-3 lg:flex-row lg:items-center", className)}>{children}</div>
}

export function SearchInput({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string }) {
  return (
    <div className={cn("relative min-w-0 lg:w-72", className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-8 pl-8 text-[13px]" />
    </div>
  )
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T
  onChange: (v: NoInfer<T>) => void
  options: { value: NoInfer<T>; label: string; count?: number }[]
  className?: string
}) {
  return (
    <div className={cn("flex gap-1 overflow-x-auto", className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground",
            value === o.value && "bg-[hsl(228_8%_13%)] text-foreground",
          )}
        >
          {o.label}
          {typeof o.count === "number" && (
            <span className="rounded bg-[hsl(228_8%_16%)] px-1.5 text-[11px] tabular-nums text-muted-foreground">{o.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

// ── Tables ────────────────────────────────────────────────────────────────────

/** Plain table styled by the shell CSS; wrap rows with `<tr className={rowClass}>`. */
export function DataTable({ children, minWidth = 760 }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left" style={{ minWidth }}>{children}</table>
    </div>
  )
}

export const th = "px-4 py-0"
export const td = "px-4 py-3 align-middle"
export const rowClass = "group cursor-pointer border-t border-border first:border-t-0"

export function usePagination<T>(items: T[], pageSize = 12) {
  const [page, setPage] = useState(1)
  const pages = Math.max(1, Math.ceil(items.length / pageSize))
  useEffect(() => {
    if (page > pages) setPage(pages)
  }, [page, pages])
  const slice = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize])
  return { page, pages, setPage, slice, total: items.length, pageSize, reset: () => setPage(1) }
}

export function Pagination({ page, pages, total, pageSize, setPage, noun = "registros" }: { page: number; pages: number; total: number; pageSize: number; setPage: (p: number) => void; noun?: string }) {
  if (total === 0) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5 text-[12px] text-muted-foreground">
      <span className="tabular-nums">
        {from}–{to} de {total} {noun}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Página anterior">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-12 text-center tabular-nums text-foreground">
          {page} / {pages}
        </span>
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Próxima página">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// ── Empty / loading ───────────────────────────────────────────────────────────

export function EmptyState({ icon: Icon, title, description, action }: { icon: LucideIcon; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-[hsl(228_8%_12%)] text-muted-foreground">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-[14px] font-medium text-foreground">{title}</p>
        {description && <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-[hsl(228_8%_10%)]" />
      ))}
    </div>
  )
}

// ── Detail drawer ─────────────────────────────────────────────────────────────

export function DetailSheet({
  open,
  onOpenChange,
  title,
  subtitle,
  badge,
  actions,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto border-border bg-[hsl(228_11%_6.5%)] p-0 sm:max-w-[480px]">
        <div className="space-y-3 border-b border-border p-5 pr-12">
          {badge}
          <SheetTitle className="text-[17px] font-semibold leading-snug">{title}</SheetTitle>
          {subtitle && <SheetDescription asChild><div className="text-[13px] text-muted-foreground">{subtitle}</div></SheetDescription>}
          {actions && <div className="flex flex-wrap gap-2 pt-1">{actions}</div>}
        </div>
        <div className="flex-1 space-y-6 p-5">{children}</div>
      </SheetContent>
    </Sheet>
  )
}

export function DetailSection({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{title}</h4>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Label/value rows inside a bordered box. Empty values render as "—". */
export function DetailList({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="divide-y divide-border rounded-lg border border-border">
      {items.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 px-3 py-2.5 text-[13px]">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words text-foreground">{value === null || value === undefined || value === "" ? <span className="text-muted-foreground">—</span> : value}</dd>
        </div>
      ))}
    </dl>
  )
}

// ── Formatting ────────────────────────────────────────────────────────────────

export function fmtDateBR(value?: string | null, withTime = false) {
  if (!value) return "—"
  const d = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return withTime ? d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : d.toLocaleDateString("pt-BR")
}

/** "há 3 dias", "em 12 dias", "hoje". */
export function relativeDays(value?: string | null) {
  if (!value) return null
  const d = new Date(value)
  const diff = Math.round((d.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86_400_000)
  if (diff === 0) return "hoje"
  if (diff === 1) return "amanhã"
  if (diff === -1) return "ontem"
  return diff > 0 ? `em ${diff} dias` : `há ${-diff} dias`
}

export function initials(name?: string | null) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

export function Avatar({ name, className }: { name?: string | null; className?: string }) {
  return (
    <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[hsl(228_8%_15%)] text-[11px] font-semibold text-[hsl(225_8%_80%)]", className)}>
      {initials(name)}
    </span>
  )
}
